import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { X, Save, Cog, Plus, Trash2, RulerDimensionLine, Check } from 'lucide-react';

const TypeForm = ({ isOpen, onClose, onSuccess, editingType = null }) => {
  const { userRole } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    specsIds: []
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [specs, setSpecs] = useState([]);
  const [specsLoading, setSpecsLoading] = useState(false);

  // Load specs from Firestore when form opens
  useEffect(() => {
    if (isOpen) {
      fetchSpecs();
    }
  }, [isOpen]);

  const fetchSpecs = async () => {
    try {
      setSpecsLoading(true);
      const q = query(collection(db, 'specs'), orderBy('prioridad'));
      const querySnapshot = await getDocs(q);
      const specsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSpecs(specsList);
    } catch (error) {
      console.log('Specs collection may not exist yet, starting with empty list');
      setSpecs([]);
    } finally {
      setSpecsLoading(false);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (editingType) {
      setFormData({
        nombre: editingType.nombre || '',
        specsIds: editingType.specsIds || []
      });
    } else {
      // Reset form for new type
      setFormData({
        nombre: '',
        specsIds: []
      });
    }
    setErrors({});
  }, [editingType, isOpen]);

  const validateForm = async () => {
    const newErrors = {};

    // Validate type name
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre del tipo de equipo es requerido';
    } else {
      // Check for duplicate type names
      try {
        const typesQuery = query(collection(db, 'tipos_equipo'));
        const typesSnapshot = await getDocs(typesQuery);
        
        const duplicateFound = typesSnapshot.docs.some(doc => {
          const existingType = doc.data();
          const isDifferentType = !editingType || doc.id !== editingType.id;
          return isDifferentType && 
                 existingType.nombre?.toLowerCase() === formData.nombre.trim().toLowerCase();
        });

        if (duplicateFound) {
          newErrors.nombre = 'Ya existe un tipo de equipo con este nombre';
        }
      } catch (error) {
        // Handle case where collection doesn't exist yet - this is normal for first-time use
        console.log('Tipos_equipo collection may not exist yet, skipping duplicate check');
        // Don't set an error since this is expected behavior when no types exist yet
      }
    }

    // Validate specifications
    if (formData.specsIds.length === 0) {
      newErrors.specsIds = 'Debe seleccionar al menos una especificación';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSpecToggle = (specId) => {
    setFormData(prev => {
      const isSelected = prev.specsIds.includes(specId);
      const updatedSpecsIds = isSelected 
        ? prev.specsIds.filter(id => id !== specId)
        : [...prev.specsIds, specId];
      
      return {
        ...prev,
        specsIds: updatedSpecsIds
      };
    });

    // Clear specifications error when user makes selection
    if (errors.specsIds) {
      setErrors(prev => ({
        ...prev,
        specsIds: ''
      }));
    }
  };

  const isSpecSelected = (specId) => {
    return formData.specsIds.includes(specId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    // Check permissions
    if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
      setSubmitError(`No tienes permisos para ${editingType ? 'editar' : 'crear'} tipos de equipo. Tu rol actual es: ${userRole || 'sin rol'}`);
      return;
    }

    setLoading(true);

    try {
      // Clean up data
      const cleanedData = {
        nombre: formData.nombre.trim(),
        specsIds: formData.specsIds,
        updatedAt: new Date()
      };

      if (editingType) {
        // Update existing type
        await updateDoc(doc(db, 'tipos_equipo', editingType.id), cleanedData);
      } else {
        // Create new type
        cleanedData.createdAt = new Date();
        await addDoc(collection(db, 'tipos_equipo'), cleanedData);
      }
      
      // Invalidate equipment type cache to ensure fresh data everywhere
      try {
        const { invalidateCache } = await import('../../lib/cacheService');
        invalidateCache('types');
      } catch (error) {
        console.error('Error invalidating equipment type cache:', error);
      }
      
      // Reset form and close
      resetForm();
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving equipment type:', error);
      setSubmitError('Error al guardar el tipo de equipo. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      specsIds: []
    });
    setErrors({});
    setSubmitError('');
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const getSelectedSpecsCount = () => {
    return formData.specsIds.length;
  };

  const getSelectedSpecsNames = () => {
    return formData.specsIds.map(id => {
      const spec = specs.find(s => s.id === id);
      return spec ? spec.nombre : 'Especificación desconocida';
    }).join(', ');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Cog className="h-6 w-6 text-green-600 dark:text-green-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingType ? 'Editar Tipo de Equipo' : 'Nuevo Tipo de Equipo'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
              {submitError}
            </div>
          )}
          <div className="space-y-6">
            {/* Type Name */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre del Tipo de Equipo *
              </label>
              <input
                type="text"
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.nombre ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Ej: Bomba Sumergible, Motor Eléctrico, Control Box..."
                disabled={loading}
              />
              {errors.nombre && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nombre}</p>
              )}
            </div>

            {/* Specifications */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Especificaciones Técnicas *
                </label>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {getSelectedSpecsCount()} seleccionada{getSelectedSpecsCount() !== 1 ? 's' : ''}
                </div>
              </div>
              
              {errors.specsIds && (
                <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-3 py-2 rounded-md text-sm">
                  {errors.specsIds}
                </div>
              )}
              
              {/* Selected specs summary */}
              {getSelectedSpecsCount() > 0 && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-400">
                    <strong>Especificaciones seleccionadas:</strong> {getSelectedSpecsNames()}
                  </p>
                </div>
              )}
              
              {specsLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Cargando especificaciones...
                </div>
              ) : specs.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <RulerDimensionLine className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">No hay especificaciones</p>
                  <p>Primero debe crear especificaciones en la gestión de especificaciones.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                  {specs.map((spec) => (
                    <div key={spec.id} className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        id={`spec-${spec.id}`}
                        checked={isSpecSelected(spec.id)}
                        onChange={() => handleSpecToggle(spec.id)}
                        disabled={loading}
                        className="h-4 w-4 text-green-600 dark:text-green-400 focus:ring-green-500 dark:focus:ring-green-400 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 dark:bg-gray-600"
                      />
                      <label 
                        htmlFor={`spec-${spec.id}`}
                        className="ml-3 flex-1 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {spec.nombre}
                            </p>
                            {spec.descripcion && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                {spec.descripcion}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400">
                              #{spec.prioridad}
                            </span>
                            {isSpecSelected(spec.id) && (
                              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-400">
                  <strong>Nota:</strong> Las especificaciones seleccionadas se utilizarán para definir los campos técnicos 
                  que aparecerán en los formularios de equipos de este tipo. El orden de prioridad se mantiene 
                  según se definió en la gestión de especificaciones.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingType ? 'Actualizar Tipo' : 'Guardar Tipo')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TypeForm;